import { useCallback } from "react";
import { getRandomText } from "../../../../../components/common/SurpriseButton/SurpriseButton";
import { POSTHOG_EVENTS } from "../../../../../constants/PostHogEvents";
import usePostHog from "../../../../../hooks/usePostHog";
import { useAppStore } from "../../../../../hooks/useStores";
import { getFiboLabVariant, isFibo2App, isFiboBbqApp } from "../../../../../utils";
import { useFiboUsage } from "../useFiboUsage/useFiboUsage";

type UseFiboActionsProps = {
	activeTab: "generate" | "refine";
	isRefineTabEnabled: boolean;
};

const isAnonymousUsageLimitError = (error: any): boolean => {
	return error?.response?.status === 429 && error?.response?.data?.code === "ANONYMOUS_USAGE_LIMIT_REACHED";
};

const getAnonymousLimitFeature = (error: any): string => {
	return error?.response?.data?.feature || "labs_generate";
};

export const useFiboActions = ({ activeTab, isRefineTabEnabled }: UseFiboActionsProps) => {
	const { textToImageStore, fiboGenerateStore, fiboStore, authStore, anonymousUsageStore } = useAppStore();
	const { makeHandleActionClick } = useFiboUsage();
	const { trackEvent } = usePostHog();
	const labVariant = getFiboLabVariant();
	const isAnonymous = !authStore.isLoggedIn;
	const isBbq = isFiboBbqApp();
	const isFibo2 = isFibo2App();
	const generateFeature = isBbq ? "labs_bbq_generate" : isFibo2 ? "labs_fibo2_generate" : "labs_generate";
	const refineFeature = isBbq ? "labs_bbq_refine" : isFibo2 ? "labs_fibo2_refine" : "labs_refine";

	const hasPromptOrImage = textToImageStore.config?.prompt || textToImageStore.imageReference.file !== undefined;
	const hasJsonInput = isFibo2 && fiboStore.inputMode === "json";
	const hasPrompt = !!textToImageStore.config?.prompt;
	// FIBO 2: a structural/text VGL edit (bbox/color/keypoint/name/description) is enough to
	// enable Refine even with no instruction — those apply via generate.
	const isVglModified = isFibo2 && fiboStore.isSelectedGroupStructuredPromptModified();
	// FIBO 2: an embed area holding an image also enables Refine — it composites into the
	// scene instead of applying a vgl edit. Both halves are required, so the button can
	// never arm with no target_bbox to send (same condition as the refine routing).
	const { embedFile, embedBbox } = fiboStore.structuredPromptStore;
	const hasEmbedTarget = isFibo2 && !!embedFile && !!embedBbox;

	const isButtonDisabled =
		activeTab === "generate"
			? !hasPromptOrImage && !hasJsonInput
			: isFibo2App()
				? !isRefineTabEnabled || (!hasPrompt && !hasJsonInput && !isVglModified && !hasEmbedTarget)
				: isFiboBbqApp()
					? !isRefineTabEnabled
					: !isRefineTabEnabled || !hasPromptOrImage;

	const buildEventProperties = useCallback(() => {
		const config = textToImageStore.config;
		return {
			prompt: config?.prompt,
			structured_prompt: config?.structured_prompt,
			aspect_ratio: config?.aspect_ratio,
			steps: config?.steps_num,
			seed: config?.seed,
			has_negative_prompt: !!config?.negative_prompt,
			num_results: config?.num_results,
			has_image_reference: !!textToImageStore.imageReference?.file,
		};
	}, [textToImageStore.config, textToImageStore.imageReference]);

	const handleSurpriseClick = useCallback(() => {
		const surpriseText = activeTab === "generate" ? getRandomText("fiboGenerate") : getRandomText("fiboRefine");
		textToImageStore.handleConfigChange("prompt", surpriseText);
	}, [activeTab, textToImageStore]);

	const handleGenerateClick = useCallback(async () => {
		try {
			if (isButtonDisabled) {
				return;
			}

			// Cap num_results to remaining tokens for anonymous users
			if (isAnonymous) {
				const remaining = anonymousUsageStore.getRemaining(generateFeature);
				if (remaining <= 0) {
					anonymousUsageStore.setLimitReachedFeature(generateFeature);
					return;
				}
				const requested = textToImageStore.config.num_results ?? 1;
				if (requested > remaining) {
					textToImageStore.handleConfigChange("num_results", remaining);
				}
			}

			// Track button click
			if (labVariant) {
				trackEvent(POSTHOG_EVENTS.FIBO_GENERATE_BUTTON_CLICKED, {
					lab_variant: labVariant,
					...buildEventProperties(),
				});
			}

			const startProperties = {
				lab_variant: labVariant || "FIBO",
				...buildEventProperties(),
			};
			trackEvent(POSTHOG_EVENTS.FIBO_GENERATE_STARTED, startProperties);

			await fiboGenerateStore.generate();

			const completedProperties = {
				lab_variant: labVariant || "FIBO",
				...buildEventProperties(),
			};
			trackEvent(POSTHOG_EVENTS.FIBO_GENERATE_COMPLETED, completedProperties);
			makeHandleActionClick();
		} catch (error: any) {
			if (isAnonymousUsageLimitError(error)) {
				anonymousUsageStore.setLimitReachedFeature(getAnonymousLimitFeature(error));
				return;
			}
			console.error("Failed to generate image:", error);
			trackEvent(POSTHOG_EVENTS.FIBO_GENERATE_FAILED, {
				lab_variant: labVariant || "FIBO",
				...buildEventProperties(),
				error: error?.toString(),
			});
		}
	}, [
		isButtonDisabled,
		buildEventProperties,
		trackEvent,
		textToImageStore,
		makeHandleActionClick,
		labVariant,
		anonymousUsageStore,
		isAnonymous,
	]);

	const handleRefineClick = useCallback(async () => {
		try {
			if (isButtonDisabled) {
				return;
			}

			// Cap num_results to remaining tokens for anonymous users
			if (isAnonymous) {
				const remaining = anonymousUsageStore.getRemaining(refineFeature);
				if (remaining <= 0) {
					anonymousUsageStore.setLimitReachedFeature(refineFeature);
					return;
				}
				const requested = textToImageStore.config.num_results ?? 1;
				if (requested > remaining) {
					textToImageStore.handleConfigChange("num_results", remaining);
				}
			}

			// Track button click
			if (labVariant) {
				trackEvent(POSTHOG_EVENTS.FIBO_REFINE_BUTTON_CLICKED, {
					lab_variant: labVariant,
					...buildEventProperties(),
				});
			}

			const selectedGroup = fiboStore.getSelectedFiboGroup();
			if (selectedGroup?.structured_prompt) {
				fiboStore.handleStructuredPromptChange(selectedGroup.structured_prompt);
			}

			const startProperties = {
				lab_variant: labVariant || "FIBO",
				...buildEventProperties(),
			};
			trackEvent(POSTHOG_EVENTS.FIBO_REFINE_STARTED, startProperties);

			await fiboStore.refine();
			makeHandleActionClick();

			const completedProperties = {
				lab_variant: labVariant || "FIBO",
				...buildEventProperties(),
				structured_prompt_diff: textToImageStore.config?.structured_prompt_diff,
			};
			trackEvent(POSTHOG_EVENTS.FIBO_REFINE_COMPLETED, completedProperties);
		} catch (error: any) {
			if (isAnonymousUsageLimitError(error)) {
				anonymousUsageStore.setLimitReachedFeature(getAnonymousLimitFeature(error));
				return;
			}
			console.error("Failed to refine image:", error);
			trackEvent(POSTHOG_EVENTS.FIBO_REFINE_FAILED, {
				lab_variant: labVariant || "FIBO",
				...buildEventProperties(),
				structured_prompt_diff: textToImageStore.config?.structured_prompt_diff,
				error: error?.toString(),
			});
		}
	}, [
		isButtonDisabled,
		buildEventProperties,
		trackEvent,
		textToImageStore,
		fiboStore,
		makeHandleActionClick,
		labVariant,
		anonymousUsageStore,
		isAnonymous,
	]);

	return {
		isButtonDisabled,
		handleGenerateClick,
		handleRefineClick,
		handleSurpriseClick,
	};
};
